import moment from 'moment';
import { FailedProcessReportType } from 'src/AngularJS/Admin/PipelineConfiguration/ProcessReports/failed-process-reports-modal.component';
import { ApiResponse } from 'src/AngularJS/Models/api-response.model';
import { GroupType } from 'src/AngularJS/Models/group-type.model';
import { Group } from 'src/AngularJS/Models/group.model';
import DefinitionStatus from 'src/shared/models/pipeline-config/definition-status.model';
import { HierarchySetDefinition } from 'src/shared/models/pipeline-config/hierarchy-definition.model';
import { MetricDefinition, MetricImage } from "src/shared/models/pipeline-config/metric-definition.models";
import { ComparisonType } from 'src/shared/models/pipeline-config/mongo-enums.model';
import { RawDataDefinition, RawDataDefinitionWSTObj } from 'src/shared/models/pipeline-config/raw-data-definition.models';
import { RecalculationOptions, RecalculationStatusApiModel, RecalculationValidation } from 'src/shared/models/pipeline-config/recalculation.models';
import { ReportingSetDefinition } from "src/shared/models/pipeline-config/reporting-sets.models";
import { CachedRequestPromise, CacheState, DataFeedReverseDependencies, FailedProcessReport, PipelineConfigPermissionsModel, SimulatePipelineResult } from '../models/pipeline-config/pipeline-configuration.models';
import { ClearViewUtils } from '../utils';
import { CvApiServiceBase } from './cv-api-service-base';
import { CvLocalizationServiceBase } from './cv-localization-service-base';

/** This service provides the functions to communicate with the Pipeline Configuration endpoints. */
export default class PipelineConfigurationServiceBase {

    // Used in markup with ng-if to show the form
    public editMetricDefinition: boolean = false;

    // Used in the PipelineConfigurationMain.aspx template markup to pass through to edit-metric
    public metricDefToEdit: MetricDefinition = null;

    // Used in markup with ng-if to show the form
    public editRawDataDefinition: boolean = false;
    public rawDataDefToEdit: RawDataDefinitionWSTObj = null;

    // In display order, not ComparisonType order
    // This is an array of arrays in order to guarantee ordering for <select ng-options>
    public numericComparisonTypes: [ComparisonType, string][] = [
        [ComparisonType.Equal, "="],
        [ComparisonType.NotEqual, "\u2260"],
        [ComparisonType.LessThan, "<"],
        [ComparisonType.GreaterThan, ">"],
        [ComparisonType.GreaterThanOrEqual, "\u2265"],
        [ComparisonType.LessThanOrEqual, "\u2264"],
    ];

    // Strings have Equal, Not Equal, Contain, StartWith, and EndWith.
    public stringComparisonTypes: [ComparisonType, string][] = [
        ...this.numericComparisonTypes.slice(0, 2),
        [ComparisonType.Contain, "contains"],
        [ComparisonType.StartWith, "starts with"],
        [ComparisonType.EndWith, "ends with"],
    ];

    // Used in the Metric Component Preview
    // This is distinct from the above, since it's used as a lookup map.  Based on the ComparisonType, get its symbol.
    public displayComparisonTypes: { [key: number]: string } = {
        [ComparisonType.Equal]: "=",
        [ComparisonType.NotEqual]: "\u2260",
        [ComparisonType.GreaterThan]: ">",
        [ComparisonType.GreaterThanOrEqual]: "\u2265",
        [ComparisonType.LessThan]: "<",
        [ComparisonType.LessThanOrEqual]: "\u2264",
        // The localized values for these are instead used in MetricComponentPreviewComponent.formatCompValue():
        [ComparisonType.Contain]: "contains",
        [ComparisonType.StartWith]: "starts with",
        [ComparisonType.EndWith]: "ends with",
    };

    // This map caches the distinct values for a reporting set
    // Since the reporting set data pull requires the date field be specified, and the distinct values are only over the previous 14 days,
    // this cache maps stores the values based on the date field.
    // { Reporting Set: { Data Field: { Date Field: [distinct values] } } }
    private distinctValueCache: Map<string, Map<string, Map<string, string[]>>> = new Map<string, Map<string, Map<string, string[]>>>();

    // These are cached so subsequent loads don't have to make a request:
    private groupTypes: GroupTypes = null;
    private metricCategories: string[] = null;
    private metricImages: MetricImage[] = null;
    private permissions: PipelineConfigPermissionsModel = null;
    private unrecognizedImportDefinitions = new CachedRequestPromise<RawDataDefinition[]>();
    private rawDataDefinitionCache = new CachedRequestPromise<RawDataDefinition[]>();
    private hierarchyDefinitionCache = new CachedRequestPromise<HierarchySetDefinition[]>();
    private reportingSetDefinitionCache = new CachedRequestPromise<ReportingSetDefinition[]>();
    private metricDefinitionCache = new CachedRequestPromise<MetricDefinition[]>();
    private metricFormulaMetricSelectorHtmlCache = new CachedRequestPromise<string>();

    // Used to calculate color of the "Last Successful Run" value displayed in Raw Data, Hierarchy, and Reporting Set Definition landing pages:
    private twoWeeksAgo = moment().utc().subtract(14, "days");
    private areComparisonTypesLocalized: boolean = false;

    constructor(
        fromSharedService: boolean = false,
        private CvApiService?: CvApiServiceBase,
        private CvLocalizationService?: CvLocalizationServiceBase,
    ) {
        if (fromSharedService)
            return PipelineConfigurationServiceBase.getServiceBase();

        this.CvApiService = this.CvApiService ?? CvApiServiceBase.getServiceBase();
        this.CvLocalizationService = this.CvLocalizationService ?? CvLocalizationServiceBase.getServiceBase();
    }

    static getServiceBase(): PipelineConfigurationServiceBase {
        return ClearViewUtils.getSharedService(PipelineConfigurationServiceBase, 'PipelineConfigurationServiceBase');
    }

    /**
     * Used to update `this.stringComparisonTypes` with the localized values for Contain, StartWith, EndWith.
     * Call this after ensuring CVLocalizationService has loaded Pipeline_Configuration.
     */
    public localizeComparisonTypes() {
        if (this.areComparisonTypesLocalized)
            return;

        let comparisonType = this.stringComparisonTypes.find(ct => ct[0] == ComparisonType.Contain);
        comparisonType[1] = this.CvLocalizationService.getCachedText("Pipeline_Configuration", "ComparisonType_Contain", "contains");

        comparisonType = this.stringComparisonTypes.find(ct => ct[0] == ComparisonType.StartWith);
        comparisonType[1] = this.CvLocalizationService.getCachedText("Pipeline_Configuration", "ComparisonType_StartWith", "starts with");

        comparisonType = this.stringComparisonTypes.find(ct => ct[0] == ComparisonType.EndWith);
        comparisonType[1] = this.CvLocalizationService.getCachedText("Pipeline_Configuration", "ComparisonType_EndWith", "ends with");

        this.areComparisonTypesLocalized = true;
    }

    /**
     * Shows the <edit-metric> component in PipelineConfigurationMain.aspx.
     * @param metricDefinition Metric definition to edit
     */
    public showEditMetric(metricDefinition?: MetricDefinition) {
        this.metricDefToEdit = metricDefinition;
        this.editMetricDefinition = true;
    }

    /**
     * Shows the <edit-raw-data> component in PipelineConfigurationMain.aspx.
     * @param rawDataDefinition Raw Data Definition to edit
     */
    public showEditRawData(rawDataDefinition?: RawDataDefinitionWSTObj) {
        this.rawDataDefToEdit = rawDataDefinition;
        this.editRawDataDefinition = true;
    }

    /**
     * Returns to showing the Metric Definitions list.
     */
    public closeEditMetric() {
        this.editMetricDefinition = false;
        this.metricDefToEdit = null;
    }

    /**
     * Closes <edit-raw-data> and displays the Raw Data Landing page
     */
    public closeEditRawData() {
        this.editRawDataDefinition = false;
        this.rawDataDefToEdit = null;
    }

    /**
     * Loads metric images.  Returns the cached metric images if this has been called before.
     */
    public async getMetricImages(): Promise<MetricImage[]> {
        if (this.metricImages != null)
            return this.metricImages;

        try {
            // ClearView WebPortal/AppCode/Api/Controllers/Pipeline/PipelineDataController.cs
            let response = await this.CvApiService.get<MetricImage[]>("/api/v2/pipeline/metric-definition/metric-images");
            if (this.metricImages != null)
                return this.metricImages; // in case another dispatch finished before

            if (response.status_code == 200) {
                this.metricImages = response.data;
                return this.metricImages;
            } else {
                throw response;
            }
        } catch (exception) {
            console.error("Could not get metric images:", exception);
            return [];
        }
    }

    /**
     * Checks if the provided formula string is valid for a Calculated Metric.
     * @param formula The content of the Metric Formula component's <textarea>
     */
    public async postValidateFormula(formula: string) {
        if (formula == null)
            return false;

        let postData = { formula };
        // ClearView WebPortal/AppCode/Api/Controllers/Pipeline/PipelineDataController.cs
        let response = await this.CvApiService.post<boolean>("/api/v2/pipeline/metric-definition/validate-formula", postData);
        if (response.status_code == 200)
            return response.data;
        else
            throw response;
    }

    /**
     * Loads Unrecognized Imports as Raw Data Definitions for the current company.
     */
    public async getUnrecognizedImports(): Promise<RawDataDefinition[]> {
        if (this.unrecognizedImportDefinitions.state == CacheState.Unloaded)
            this.unrecognizedImportDefinitions.setPromise(this.CvApiService.get<RawDataDefinition[]>("/api/v1/pipeline-config/raw-data-unrecognized"));

        try {
            let response = await this.unrecognizedImportDefinitions.promise;
            if (response.status_code == 200)
                return JSON.parse(JSON.stringify(response.data));
            throw response;
        } catch (ex) {
            console.error("Could not retrieve unrecognized imports as definitions: ", ex);
            this.unrecognizedImportDefinitions.clear();
        }
    }

    /**
     * Loads the Raw Data Definitions for the current company.
     */
    public async getRawDataDefinitions(): Promise<RawDataDefinition[]> {
        if (this.rawDataDefinitionCache.state == CacheState.Unloaded) // the promise is stored so its response is reused like a cached object.
            this.rawDataDefinitionCache.setPromise(this.CvApiService.get<RawDataDefinition[]>("/api/v2/pipeline/raw-data-definitions"));

        try {
            let response = await this.rawDataDefinitionCache.promise;
            if (response.status_code == 200)
                return JSON.parse(JSON.stringify(response.data)); // Copy, since the promise will be kept around to cache the response
            throw response;
        } catch (ex) {
            console.error("Could not retrieve raw data definitions:", ex);
            this.rawDataDefinitionCache.clear();
            throw ex;
        }
    }

    /**
     * Loads the viewable Hierarchy Definitions for the current company.
     */
    public async getHierarchyDefinitions(): Promise<HierarchySetDefinition[]> {
        if (this.hierarchyDefinitionCache.state == CacheState.Unloaded)
            this.hierarchyDefinitionCache.setPromise(this.CvApiService.get<HierarchySetDefinition[]>("/api/v1/pipeline-config/hierarchy-definitions"));

        try {
            let response = await this.hierarchyDefinitionCache.promise;
            if (response.status_code == 200)
                return JSON.parse(JSON.stringify(response.data));
            throw response;
        } catch (ex) {
            console.error("Could not retrieve hierarchy definitions:", ex);
            this.hierarchyDefinitionCache.clear();
            throw ex;
        }
    }

    /**
     * Loads the Reporting Sets for the current company.
     */
    public async getReportingSets(): Promise<ReportingSetDefinition[]> {
        if (this.reportingSetDefinitionCache.state == CacheState.Unloaded)
            this.reportingSetDefinitionCache.setPromise(this.CvApiService.get<ReportingSetDefinition[]>("/api/v1/pipeline-config/reporting-set-definitions"));

        try {
            let response = await this.reportingSetDefinitionCache.promise;
            if (response.status_code == 200)
                return JSON.parse(JSON.stringify(response.data));
            throw response;
        } catch (ex) {
            console.error("Could not retrieve reporting set definitions:", ex);
            this.reportingSetDefinitionCache.clear();
            throw ex;
        }
    }

    /**
     * Loads the active Metric Definitions for the current company.
     */
    public async getMetricDefinitions(): Promise<MetricDefinition[]> {
        if (this.metricDefinitionCache.state == CacheState.Unloaded) // the promise is stored so its response is reused like a cached object.
            this.metricDefinitionCache.setPromise(this.CvApiService.get<MetricDefinition[]>("/api/v1/pipeline-config/metric-definitions"));

        try {
            let response = await this.metricDefinitionCache.promise;
            if (response.status_code == 200)
                return JSON.parse(JSON.stringify(response.data));
            throw response;
        } catch (ex) {
            console.error("Could not retrieve metric definitions:", ex);
            this.metricDefinitionCache.clear();
            throw ex;
        }
    }

    /** Clears the Raw Data definition cache */
    public clearRawDataDefinitionCache() {
        if (this.rawDataDefinitionCache.state == CacheState.Finished)
            this.rawDataDefinitionCache.clear();
    }

    /** Clears the Hierarchy definition cache */
    public clearHierarchyDefinitionCache() {
        if (this.hierarchyDefinitionCache.state == CacheState.Finished)
            this.hierarchyDefinitionCache.clear();
    }

    /** Clears the Reporting Set definition cache */
    public clearReportingSetDefinitionCache() {
        if (this.reportingSetDefinitionCache.state == CacheState.Finished)
            this.reportingSetDefinitionCache.clear();
    }

    /** Clears the Metric definition cache */
    public clearMetricDefinitionCache() {
        if (this.metricDefinitionCache.state == CacheState.Finished)
            this.metricDefinitionCache.clear();
    }

    /**
     * Loads the GroupDTO objects currently assigned to the metric.
     * @param metricID The numeric MetricID value of the metric definition, NOT the _id string value.
     */
    public async getMetricAssignments(metricID: number) {
        // ClearView WebPortal/AppCode/Api/Controllers/Pipeline/PipelineDataController.cs
        let response = await this.CvApiService.get<Group[]>(`/api/v2/pipeline/metric-definition/assignments/${metricID}`);
        if (response.status_code == 200)
            return response.data;
        else
            throw response;
    }

    /**
     * Saves the provided Metric Definition object.
     * The endpoint differentiates between an edited or a new metric based on the MetricID value.
     * @param metricDefinition Metric Definition object to save.
     */
    public async postSaveMetricDefinition(metricDefinition: MetricDefinition) {
        // ClearView WebPortal/AppCode/Api/Controllers/Pipeline/PipelineDataController.cs
        let response = await this.CvApiService.post<SaveMetricResponse>("/api/v2/pipeline/metric-definition/save-definition", metricDefinition);
        // 200 on edited, 201 on created:
        if (response.status_code >= 200 && response.status_code < 300) {
            this.metricDefinitionCache.clear();
            this.addMetricCategory(metricDefinition.Group);
            return response.data;
        }
        throw response;
    }

    /**
     * Saves the provided Raw Data Definition object.
     * An edit is done if the FeedName and SourceSystems point to an existing definition, otherwise a new definition will be created.
     * @param rawDataDefinition Raw Data Definition object to validate and upsert
     */
    public async postSaveRawDataDefinition(rawDataDefinition: RawDataDefinition) {
        let response = await this.CvApiService.post<RawDataDefinitionValidation>("/api/v2/pipeline/raw-data-definition/save-definition", rawDataDefinition);
        // 200 on edited, 201 on created:
        if (response.status_code >= 200 && response.status_code < 300) {
            this.rawDataDefinitionCache.clear();
            this.unrecognizedImportDefinitions.clear();
            return response.data;
        }
        throw response;
    }

    /**
     * Loads the group types for the current company.
     */
    public async getGroupTypes() {
        if (this.groupTypes)
            return JSON.parse(JSON.stringify(this.groupTypes));

        // ClearView WebPortal/AppCode/Api/Controllers/Pipeline/PipelineDataController.cs
        let response = await this.CvApiService.get<GroupTypes>(`/api/v2/pipeline/metric-definition/group-types`);
        if (response.status_code == 200) {
            this.groupTypes = response.data;
            return JSON.parse(JSON.stringify(response.data));
        } else {
            throw response;
        }
    }

    /**
     * Loads the "Categories" (MetricDefinition.Group property) of other active metric definitions.
     */
    public async getMetricCategories() {
        if (this.metricCategories)
            return this.metricCategories;

        try {
            let response = await this.CvApiService.get<string[]>(`/api/v2/pipeline/metric-definition/metric-categories`);
            if (response.status_code == 200) {
                this.metricCategories = response.data;
                return response.data;
            } else {
                throw response;
            }
        } catch (ex) {
            console.warn("Failed loading metric categories:", ex);
            return [];
        }
    }

    /**
     * Adds the specified category to the internal cache of metric categories, if it's not already in there.
     * @param category Category to add.
     */
    private addMetricCategory(category: string): void {
        if (category && this.metricCategories && this.metricCategories.findIndex(c => c == category) == -1) {
            this.metricCategories.push(category);
            this.metricCategories.sort();
        }
    }

    /**
     * Returns a list of distinct values from the reporting set
     * @param reportingSet String name of the reporting set to pull from
     * @param dataField String name of the data field.  Distinct values come from here.
     * @param dateField String name of the date field
     */
    public async getDistinctValuesForDataField(reportingSet: string, dataField: string, dateField: string, limit?: number): Promise<string[]> {
        let reportingSetCache = this.distinctValueCache.get(reportingSet) ?? new Map<string, Map<string, string[]>>();
        this.distinctValueCache.set(reportingSet, reportingSetCache);

        let dataFieldCache = reportingSetCache.get(dataField) ?? new Map<string, string[]>();
        reportingSetCache.set(dataField, dataFieldCache);

        let distinctValues: string[] = dataFieldCache.get(dateField);
        if (distinctValues)
            return distinctValues;

        let encodedReportingSet = encodeURIComponent(reportingSet);
        let encodedDataField = encodeURIComponent(dataField);
        let encodedDateField = encodeURIComponent(dateField ?? "");
        let dateFieldParameter = encodedDateField && `?dateField=${encodedDateField}` || "";
        let limitQueryParameter = "";
        if (limit !== undefined)
            limitQueryParameter = (dateFieldParameter ? "&" : "?") + `limit=${limit}`;

        try {
            // ClearView WebPortal/AppCode/Api/Controllers/Pipeline/PipelineDataController.cs
            let response = await this.CvApiService.get<any[]>(`/api/v2/reporting-set/${encodedReportingSet}/distinct/${encodedDataField}${dateFieldParameter}${limitQueryParameter}`);
            if (response.status_code == 200) {
                distinctValues = response.data?.filter(value => typeof value === "string")
                    .sort((a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: "base" }));
                distinctValues = distinctValues ?? [];
                dataFieldCache.set(dateField, distinctValues);
                return distinctValues;
            } else {
                throw response;
            }
        } catch (ex) {
            console.warn(`Could not get distinct values for reportingSet="${reportingSet}" dataField="${dataField}" dateField="${dateField}" limit="${limit}"`, ex);
            return [];
        }
    }

    /**
     * Synchronous function that returns the distinct values from a reporting set, if they're already cached.
     * @param reportingSet String name of the reporting set to pull from
     * @param dataField String name of the data field.  Distinct values come from here.
     * @param dateField String name of the date field
     */
    public getCachedDistinctValues(reportingSet: string, dataField: string, dateField: string): string[] {
        let reportingSetCache = this.distinctValueCache.get(reportingSet) ?? new Map<string, Map<string, string[]>>();
        let dataFieldCache = reportingSetCache.get(dataField) ?? new Map<string, string[]>();
        let distinctValues = dataFieldCache.get(dateField);

        return distinctValues ?? [];
    }

    /**
     * Gets the user's permissions to the Pipeline Configuration tabs.  A reload of the page will be required if permissions change.
     */
    public async getPipelineConfigurationPermissions(): Promise<PipelineConfigPermissionsModel> {
        if (this.permissions)
            return this.permissions;

        try {
            let response = await this.CvApiService.get<PipelineConfigPermissionsModel>("/api/v1/pipeline-config/permissions");
            if (response.status_code != 200 || response.data == null)
                throw response;
            this.permissions = response.data;
            return response.data;
        } catch (ex) {
            console.warn("Could not load permissions for Pipeline Configuration");
            return { HierarchyConfiguration: false, MetricConfiguration: false, RawDataConfiguration: false, ReportingSetConfiguration: false, IsAdmin: false };
        }
    }

    /**
     * Deactivates a Metric by setting its Active property to false.  This is effectively a soft-delete.
     * @param metricID MetricID value of the metric definition to deactivate
     */
    public async deactivateMetricDefinition(metricID: number): Promise<void> {
        try {
            let result = await this.CvApiService.delete<null>("/api/v2/pipeline/metric-definition/" + metricID);
            if (result.status_code != 200)
                throw result;
        } catch (err) {
            console.error("Could not deactivate metric definition:", err);
            throw err;
        }
    }

    /**
     * Gets an array of other active metric abbreviations that are using the provided `metricID` in their formulas.
     * @param metricID MetricID value of the metric definition to look up in other Calculated Formulas
     */
    public async getMetricReverseDependencies(metricID: number): Promise<string[]> {
        try {
            let result = await this.CvApiService.get<string[]>("/api/v2/pipeline/metric-definition/rdepends/" + metricID);
            if (result.status_code != 200)
                throw result;
            return result.data ?? [];
        } catch (err) {
            console.error("Could not get metric definition reverse dependencies:", err);
            throw err;
        }
    }

    /**
     * Returns the Reporting Set Names of Reporting Sets using the specified Hierarchy Definition as a base feed or joined feeds.
     * It also returns the Metric Abbreviations of Standard or Snapshot Metrics using the Reporting Sets mentioned in the Base Feed section.
     * @param pseudoFeedName PseudoFeedName of the Hierarchy Definition
     */
    public async getHierarchyReverseDependencies(pseudoFeedName: string): Promise<DataFeedReverseDependencies> {
        try {
            const encodedFeedName = encodeURIComponent(pseudoFeedName);
            let result = await this.CvApiService.get<DataFeedReverseDependencies>(`/api/v1/pipeline-config/hierarchy-rdepends?pfn=${encodedFeedName}`);
            if (result.status_code != 200)
                throw result;
            return result.data;
        } catch (err) {
            console.error("Could not get hierarchy definition reverse dependencies:", err);
            throw err;
        }
    }

    /**
     * Returns the Metric Abbreviations of Standard or Snapshot Metrics using the specified Reporting Set.
     * @param _id _id of the Reporting Set
     */
    public async getReportingSetReverseDependencies(_id: string): Promise<string[]> {
        try {
            const encodedId = encodeURIComponent(_id);
            let result = await this.CvApiService.get<string[]>(`/api/v1/pipeline-config/reporting-set-rdepends?id=${encodedId}`);
            if (result.status_code != 200)
                throw result;
            return result.data;
        } catch (err) {
            console.error("Could not get reporting set definition reverse dependencies:", err);
            throw err;
        }
    }

    /**
     * Returns the Reporting Set Names of Reporting Sets using the specified Raw Data Definition as a base feed or joined feeds.
     * It also returns the Metric Abbreviations of Standard or Snapshot Metrics using the Reporting Sets mentioned in the Base Feed section.
     */
    public async getRawDataReverseDependencies(_id: string): Promise<DataFeedReverseDependencies> {
        try {
            const encodedId = encodeURIComponent(_id);
            let result = await this.CvApiService.get<DataFeedReverseDependencies>(`/api/v1/pipeline-config/raw-data-rdepends?id=${encodedId}`);
            if (result.status_code != 200)
                throw result;
            return result.data;
        } catch (err) {
            console.error("Could not get raw data definition reverse dependencies:", err);
            throw err;
        }
    }


    /**
     * Copies a metric and appends " (Copy)" to the name.
     * @param metricID MetricID value of the metric definition to duplicate
     */
    public async copyMetricDefinition(metricID: number): Promise<MetricDefinition> {
        try {
            let result = await this.CvApiService.post<MetricDefinition>("/api/v2/pipeline/metric-definition/copy/" + metricID);
            if (result.status_code == 201) {
                this.metricDefinitionCache.clear();
                return result.data;
            }
            throw result;
        } catch (err) {
            console.error("Could not copy metric definition: ", err);
            throw err;
        }
    }

    /**
     * Gets the status of active and past recalculations
     */
    public async getRecalculationStatus(): Promise<RecalculationStatusApiModel[]> {
        try {
            let response = await this.CvApiService.get<RecalculationStatusApiModel[]>("/api/v2/pipeline/recalculation-status");
            if (response.status_code == 200) {
                // Convert date strings to proper objects, so Ag-Grid's filter works on dates:
                let dateFields = new Set([
                    "currentRecalculationDate",
                    "dateCompleted",
                    "dateQueued",
                    "lastModifiedDate",
                ]);

                response.data?.forEach(statusModel => {
                    for (let [key, value] of Object.entries(statusModel)) {
                        // Keeps nulls as nulls
                        if (dateFields.has(key) && value != null) {
                            statusModel[key] = new Date(value);
                        }
                    }
                });

                return response.data;
            }
            throw response;
        } catch (err) {
            console.error("Could not get Recalculation Status information: ", err);
        }
    }

    /**
     * Requests a recalculation for multiple reporting sets and/or metrics.
     * @param options Parameters for the recalculation request
     */
    public async postRecalculateMultiple(options: RecalculationOptions): Promise<ApiResponse<RecalculationValidation>> {
        let response = await this.CvApiService.post<RecalculationValidation>("/api/v2/pipeline/recalculate-multiple", options);
        if (response.status_code < 500)
            return response;
        throw response;
    }

    /**
     * Gets the Failed Process Reports for display in the Failed Process Reports Modal.
     * @param type Type of Failed Process Reports to retrieve
     */
    public async getFailedProcessReports(type: FailedProcessReportType): Promise<FailedProcessReport[]> {
        let response = await this.CvApiService.get<FailedProcessReport[]>(`/api/v1/pipeline-config/failed-process-reports/${encodeURIComponent(type)}`);
        if (response.status_code == 200) {
            // Convert all ProcessStartTime values to Date objects
            response.data?.forEach(fpr => {
                fpr.ProcessStartTime = new Date(fpr.ProcessStartTime);
            });
            return response.data;
        }
        throw response;
    }

    /**
     * Compares the LastSuccessDate and LastFailureDate to decide the text color of the display values.
     * The color returned as a design-system "color-COLORNAME" string.  This is applied as a class.
     */
    public getFailureTextColor(definition: { Status: DefinitionStatus }): string {
        var red = "color-redRyder";
        var yellow = "color-midas";
        if (definition && definition.Status && definition.Status.LastFailureDate) {
            if (!definition.Status.LastSuccessDate)
                return red;
            var lastFailureDate = moment(definition.Status.LastFailureDate);
            var lastSuccessDate = moment(definition.Status.LastSuccessDate);
            if (lastFailureDate.isAfter(lastSuccessDate))
                return red;
            else if (lastFailureDate.isAfter(this.twoWeeksAgo))
                return yellow;
        }
        return "";
    }

    /**
     * Deletes the Raw Data Definition specified by `_id`, Hierarchy Definitions, and Reporting Set Definitions based on or joining to this Raw Data Definition.
     * @param _id
     */
    public async deleteRawDataCascade(_id: string): Promise<void> {
        try {
            const encodedId = encodeURIComponent(_id);
            let response = await this.CvApiService.delete(`/api/v1/pipeline-config/raw-data?id=${encodedId}`);
            if (response.status_code == 200) {
                this.unrecognizedImportDefinitions.clear();
                this.rawDataDefinitionCache.clear();
                this.hierarchyDefinitionCache.clear();
                this.reportingSetDefinitionCache.clear();
                return;
            }
            throw response;
        } catch (ex) {
            console.error("Failure deleting Raw Data Definition", "_id", _id);
            throw ex;
        }
    }

    /**
     * Deletes the Hierarchy Definition specified by `pseudoFeedName` and Reporting Set Definitions based on or joining to this Hierarchy Definition
     * @param pseudoFeedName
     */
    public async deleteHierarchyCascade(pseudoFeedName: string): Promise<void> {
        try {
            const encodedFeedName = encodeURIComponent(pseudoFeedName);
            let response = await this.CvApiService.delete(`/api/v1/pipeline-config/hierarchy?pfn=${encodedFeedName}`);
            if (response.status_code == 200) {
                this.hierarchyDefinitionCache.clear();
                this.reportingSetDefinitionCache.clear();
                return;
            }
            throw response;
        } catch (ex) {
            console.error("Failure deleting Hierarchy Definition", "pseudoFeedName", pseudoFeedName);
            throw ex;
        }
    }

    /**
     * Deletes the Reporting Set Definition with the specified `_id`
     * @param _id
     */
    public async deleteReportingSet(_id: string): Promise<void> {
        try {
            const encodedId = encodeURIComponent(_id);
            let response = await this.CvApiService.delete(`/api/v1/pipeline-config/reporting-set?id=${encodedId}`);
            if (response.status_code == 200)
                return this.clearReportingSetDefinitionCache();
            throw response;
        } catch (ex) {
            console.error("Failure deleting Reporting Set Definition", "_id", _id);
            throw ex;
        }
    }

    /**
     * Checks if the `definition` is editable or the cached permissions for the user indicates they're an administrator
     * @param definition Any object that has a boolean property named IsEditable
     */
    public isEditable(definition: { IsEditable?: boolean }) {
        return definition?.IsEditable === true || this.permissions?.IsAdmin === true;
    }

    /** Returns if the specified Hierarchy Definition is editable.  Permissions must be satisfied. */
    public isHierarchyDefinitionDeletable(definition: { IsEditable?: boolean }) {
        return (definition?.IsEditable === true || this.permissions?.IsAdmin === true) &&
            this.permissions?.HierarchyConfiguration === true;
    }

    /** Returns if the specified Raw Data Definition is editable.  Permissions must be satisfied. */
    public isRawDataDefinitionDeletable(definition: { IsEditable?: boolean }) {
        return (definition?.IsEditable === true || this.permissions?.IsAdmin === true) &&
            this.permissions?.RawDataConfiguration === true;
    }

    /** Returns if the specified Reporting Set Definition is editable.  Permissions must be satisfied. */
    public isReportingSetDefinitionDeletable(definition: { IsEditable?: boolean }) {
        return (definition?.IsEditable === true || this.permissions?.IsAdmin === true) &&
            this.permissions?.ReportingSetConfiguration === true;
    }

    /**
     * Reactivates a metric that has been marked as invalid. (Status.IsValid is false)
     * @param metricDefinitionId Value of the definition._id
     */
    public async reactivateMetricDefinition(metricDefinitionId: string) {
        let url = `/api/v2/pipeline/metric-definition/${encodeURIComponent(metricDefinitionId)}/reactivate`;
        let response = await this.CvApiService.post<void>(url);
        this.metricDefinitionCache.clear();
        return response;
    }

    /**
     * Simulates the specified reporting set and returns up to 100 results.
     * @param reportingSetId The _id value of the Reporting Set Definition
     */
    public async simulateReportingSet(reportingSetId: string): Promise<SimulatePipelineResult> {
        const url = `/api/v1/pipeline-config/simulate-reporting-set?id=${encodeURIComponent(reportingSetId)}`;
        let response = await this.CvApiService.get<SimulatePipelineResult>(url);
        if (response.status_code == 200)
            return response.data;
        throw response;
    }

    /**
     * Returns a sample of the current Raw Data values for the specified Raw Data Feed.
     * @param rawDataId The _id value of the Raw Data Definition
     */
    public async previewRawData(rawDataId: string): Promise<SimulatePipelineResult> {
        const url = `/api/v1/pipeline-config/preview-raw-data?id=${encodeURIComponent(rawDataId)}`;
        let response = await this.CvApiService.get<SimulatePipelineResult>(url);
        if (response.status_code == 200)
            return response.data;
        throw response;
    }

    /**
     * Returns the HTML of the Metric Selector.  Used by the MetricFormulaComponent in EditMetricComponent.
     */
    public async getMetricFormulaMetricSelectorHtml(): Promise<string> {
        if (this.metricFormulaMetricSelectorHtmlCache.state == CacheState.Unloaded) // the promise is stored so its response is reused like a cached object.
            this.metricFormulaMetricSelectorHtmlCache.setPromise(this.CvApiService.get<string>("/api/v1/pipeline-config/metric-formula-metric-selector"));

        try {
            let response = await this.metricFormulaMetricSelectorHtmlCache.promise;
            if (response.status_code == 200)
                return response.data;
            throw response;
        } catch (ex) {
            console.error("Could not retrieve metric formula metric selector:", ex);
            this.metricFormulaMetricSelectorHtmlCache.clear();
            throw ex;
        }
    }

    /**
     * Displays a JQuery dialog with Yes/No buttons.
     * Returns a Promise that resolves to "yes" or "no", depending on the button clicked.  Rejects on errors or when the dialog is closed otherwise.
     * @param title Title of dialog modal
     * @param message Message in the body of the modal
     * @param type Style of the dialog, like "warning" or "error"
     */
    public showYesNoDialog(title: string, message: string, type: string): Promise<"yes" | "no"> {
        let promise = new Promise<"yes" | "no">((resolve, reject) => {
            let buttonClicked = false;
            function closeCallback() {
                if (!buttonClicked)
                    reject();
            }
            let buttons = [
                {
                    "text": "Yes",
                    click: function () {
                        try {

                            buttonClicked = true;
                            $(".errorDialog")["dialog"]("close");
                            resolve("yes");
                        } catch {
                            reject();
                        }
                    }
                },
                {
                    "text": "No",
                    click: function () {
                        try {
                            buttonClicked = true;
                            $(".errorDialog")["dialog"]("close");
                            resolve("no");
                        } catch {
                            reject();
                        }
                    }
                }
            ];
            window["showConfirmDialog"](message, buttons, title, type, closeCallback);
        });

        return promise;
    };
}

export const enum MetricDefinitionValidation {
    Success = 0,
    MissingName = 1,
    MissingAbv = 2,
    MissingCategory = 3,
    MissingFormatType = 4,
    MissingTimeFormat = 5,
    MissingComponentType = 6,
    BadFormula = 7,
    MissingDefinitionForId = 8,
}

export interface SaveMetricResponse {
    Validation: MetricDefinitionValidation;
    MetricID: number;
}

export const enum RawDataDefinitionValidation {
    Success = 0,
    MissingFeedName = 1,
    MissingSourceSystem = 2,
    MissingDefinition = 3,
    ExistingDefinitionNotEditable = 4,
    BadPrimaryKeyFieldName = 5,
    BadInactiveFieldName = 6,
    PipelineValidationError = 7,
}

export const enum RecalculationRequestValidation {
    Success = 0,
    MissingStartDate = 1,
    DateRangeTooLarge = 2,
    MissingRecalculationIds = 3,
    BadMetricId = 4,
    Permission = 5,
}

export const enum CalculationCondition {
    Add = 0,
    Subtract = 1,
}

export interface GroupTypes {
    emp: GroupType[];
    rec: GroupType[];
}
